import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const ProgressHeader = () => {
  const location = useLocation();
  
  // Get current registration step for enhanced progress tracking
  const getCurrentRegistrationStep = () => {
    const savedStep = localStorage.getItem('currentRegistrationStep');
    return savedStep ? parseInt(savedStep, 10) : 1;
  };

  const steps = [
    {
      id: 1,
      label: 'Profile Selection',
      path: '/profile-selection-landing',
      shortLabel: 'Profile'
    },
    {
      id: 2,
      label: 'Registration Form',
      path: '/multi-step-registration-form',
      shortLabel: 'Registration',

    },
    {
      id: 3,
      label: 'Document Upload',
      path: '/document-upload-management',
      shortLabel: 'Documents'
    },
    {
      id: 4,
      label: 'Payment Processing',
      path: '/payment-processing',
      shortLabel: 'Payment'
    },
    {
      id: 5,
      label: 'Confirmation',
      path: '/registration-success-confirmation',
      shortLabel: 'Success'
    }
  ];

  const getCurrentStepIndex = () => {
    const currentStep = steps?.findIndex(step => step?.path === location?.pathname);
    return currentStep !== -1 ? currentStep : 0;
  };

  const currentStepIndex = getCurrentStepIndex();
  // const showProgress = location?.pathname !== '/profile-selection-landing';

  // if (!showProgress) {
  //   return null;
  // }

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepIndex, status) => {
    if (status === 'completed') {
      return <Icon name="Check" size={16} color="white" strokeWidth={2} />;
    }
    return <span className="text-sm font-medium">{stepIndex + 1}</span>;
  };

  // Enhanced step label for registration form showing current substep
  const getEnhancedStepLabel = (step, stepIndex) => {
    if (step?.path === '/multi-step-registration-form' && stepIndex === currentStepIndex) {
      const currentRegStep = getCurrentRegistrationStep();
      const substep = step?.substeps?.[currentRegStep - 1];
      return substep ? `${step?.label} - ${substep}` : step?.label;
    }
    return step?.label;
  };

  const getEnhancedShortLabel = (step, stepIndex) => {
    if (step?.path === '/multi-step-registration-form' && stepIndex === currentStepIndex) {
      const currentRegStep = getCurrentRegistrationStep();
      const substep = step?.substeps?.[currentRegStep - 1];
      return substep || step?.shortLabel;
    }
    return step?.shortLabel;
  };

  // Calculate enhanced progress percentage
  const getEnhancedProgress = () => {
    let progress = (currentStepIndex + 1) / steps?.length;
    
    // Add granular progress for registration form
    if (location?.pathname === '/multi-step-registration-form') {
      const currentRegStep = getCurrentRegistrationStep();
      const registrationProgress = (currentRegStep - 1) / 5; // 5 substeps
      progress = currentStepIndex / steps?.length + (registrationProgress / steps?.length);
    }
    
    return Math.round(progress * 100);
  };

  return (
    <div className="w-full bg-surface border-b border-border shadow-subtle">
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:pl-16">
        <div className="py-4">
          {/* Desktop Progress Bar */}
          <div className="hidden md:block">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => {
                const status = getStepStatus(index);
                const isLast = index === steps?.length - 1;

                return (
                  <div key={step?.id} className="flex items-center flex-1">
                    {/* Step Circle */}
                    <div className="flex items-center">
                      <div
                        className={`
                          flex items-center justify-center w-8 h-8 rounded-full border-2 transition-smooth
                          ${status === 'completed' 
                            ? 'bg-success border-success text-white' 
                            : status === 'current' ? 'bg-primary border-primary text-white' : 'bg-background border-border text-text-secondary'
                          }
                        `}
                      >
                        {getStepIcon(index, status)}
                      </div>
                      
                      {/* Step Label */}
                      <div className="ml-3">
                        <p
                          className={`
                            text-sm font-medium transition-smooth
                            ${status === 'current' ? 'text-primary' 
                              : status === 'completed' ? 'text-success' : 'text-text-secondary'
                            }
                          `}
                        >
                          {getEnhancedStepLabel(step, index)}
                        </p>
                        {/* Show substep progress for registration form */}
                        {step?.path === '/multi-step-registration-form' && index === currentStepIndex && (
                          <div className="mt-1">
                            <div className="flex items-center space-x-1">
                              <div className="w-16 h-1 bg-border rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary transition-smooth duration-300"
                                  style={{ width: `${(getCurrentRegistrationStep() / 2) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs text-text-secondary">
                                {getCurrentRegistrationStep()}/2
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Progress Line */}
                    {!isLast && (
                      <div className="flex-1 mx-4">
                        <div className="h-0.5 bg-border">
                          <div
                            className={`
                              h-full transition-smooth duration-300
                              ${index < currentStepIndex ? 'bg-success' : 'bg-border'}
                            `}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Progress Indicator */}
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full border-2
                    ${getStepStatus(currentStepIndex) === 'current' ? 'bg-primary border-primary text-white' : 'bg-success border-success text-white'
                    }
                  `}
                >
                  {getStepIcon(currentStepIndex, getStepStatus(currentStepIndex))}
                </div>
                <div>
                  <p className="text-sm font-medium text-primary">
                    {getEnhancedShortLabel(steps?.[currentStepIndex], currentStepIndex)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    Step {currentStepIndex + 1} of {steps?.length}
                    {location?.pathname === '/multi-step-registration-form' && (
                      <span className="ml-1">({getCurrentRegistrationStep()}/5)</span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="flex-1 mx-4">
                <div className="h-2 bg-muted rounded-full">
                  <div
                    className="h-full bg-primary rounded-full transition-smooth duration-300"
                    style={{ width: `${getEnhancedProgress()}%` }}
                  />
                </div>
              </div>
              
              <div className="text-xs font-medium text-text-secondary">
                {getEnhancedProgress()}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressHeader;